import React from 'react'
import ArticleContainer from './ArticleContainer'
import { getPost, getReadMore, getReply } from '@app-core/post/actions'
import { compose, withBeacon, withRouter } from '@hocs'
import { useAmp } from 'next/amp'
import ArticleAmpContainer from './ArticleAmpContainer'
import PostHead from './components/PostHead'
import { NotFoundError } from '@features/errors/NotFoundError'
import { ApiError } from '@libs/errorCode'
import { useSendArticlePageView } from '@features/post/hooks/useSendArticlePageView'
import { CleanPostDataProvider } from '@features/post/contexts/CleanPostDataContext'
import { RecommendedArticlesContextProvider } from '@features/post/contexts/RecommendedArticlesContext'
import { PopularArticlesContextProvider } from '@features/post/contexts/PopularArticlesContext'
import ShowADScript from '@components/ShowADScript'
import { getApolloClient } from '@libs/getApolloClient'
import { getDefaultCategories } from '@libs/categories/getCategories'
import getRecommendedArticles from './libs/getRecommendedArticles'
import getPopularArticles from './libs/getPopularArticles'
import Config from '@public-config'
import { withRNApp } from '@libs/RNApp'

import { htmlparser2 } from 'react-html-parser'
import {
  preprocessNodes,
  appendADNodes,
  removeCircularStructure,
  appendTableOfContents,
} from './libs/postTools'
import { getPostDescription } from '@libs/postTools'
import { getEventCategories } from '@libs/categories/getCategories'

function ArticlePage({ hasError, recommendedArticles, popularArticles, nodes, ...props }) {
  const isAmp = useAmp()
  useSendArticlePageView(props.postId, hasError)

  if (hasError) return <NotFoundError isApp={!!props.isApp} context="post" />

  return (
    <CleanPostDataProvider nodes={nodes}>
      <ShowADScript />
      <PostHead />
      <RecommendedArticlesContextProvider value={recommendedArticles}>
        <PopularArticlesContextProvider value={popularArticles}>
          {isAmp ? <ArticleAmpContainer {...props} /> : <ArticleContainer {...props} />}
        </PopularArticlesContextProvider>
      </RecommendedArticlesContextProvider>
    </CleanPostDataProvider>
  )
}

// `getInitialProps` can only applied at page level, so put it here to support AMP
ArticlePage.getInitialProps = async ({ store, ...ctx }) => {
  const client = getApolloClient(ctx as any)
  const { forum, website, postID, amp, articleId } = ctx.query
  const categoryId = forum ? forum : website
  const category = forum ? `forum.${forum}` : website
  let postId = category && postID ? [category, postID].join('.') : null
  if (articleId) {
    postId = `draft.${articleId}`
  }

  let hasError
  let categories = []
  let recommendedArticles = []
  let popularArticles = []
  let nodes = []

  try {
    if (!postId) throw new ApiError(`missing postId`, 400)

    const [categoriesResult] = await Promise.all([
      getDefaultCategories(),
      store.dispatch(getPost(postId)),
    ])

    if (categoriesResult) {
      categories = categoriesResult
    }

    if (postId.includes('draft') === false) {
      await store.dispatch(getReadMore())
      if (amp === '1') await store.dispatch(getReply())
    }

    if (Config.recommendedArticlesEnabled) {
      recommendedArticles = await getRecommendedArticles({
        client,
        seedId: postId,
        limit: 6,
      })
    }

    if (Config.popularArticlesEnabled && categoryId) {
      popularArticles = await getPopularArticles({
        client,
        categoryId,
      })
    }

    const description = getPostDescription(store.getState().post.getIn(['post', 'data']).toJS())
    const categoryIds = categories.map((val) => val.id)
    nodes = htmlparser2.parseDOM(description, { decodeEntities: true })
    preprocessNodes(nodes, categoryIds)
    appendADNodes(nodes)
    removeCircularStructure(nodes)
    appendTableOfContents(nodes)
  } catch (error) {
    console.error(error)
    hasError = true
    if (ctx.res) ctx.res.statusCode = error?.code ?? 500
  }

  const eventCategories = await getEventCategories(client)

  return {
    hasError,
    postId,
    categories,
    recommendedArticles,
    popularArticles,
    nodes,
    eventCategories,
  }
}

export default compose(withRNApp, withBeacon, withRouter)(ArticlePage)
